import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Camera, Plus, Trash2, Users, ExternalLink,
  Loader2, Check, X, AlertCircle, Tag, Image as ImageIcon, Crown, Sparkles, Building2
} from 'lucide-react';
import Footer from '../components/Footer';
import NotifBell from '../components/NotifBell';
import LinkifyText from '../components/LinkifyText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InfluencerPortfolioPage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  
  const [influencer, setInfluencer] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [planLimits, setPlanLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Add item state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newDescription, setNewDescription] = useState('');
  const [newTags, setNewTags] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Brand collaboration tagging state
  const [tagBrand, setTagBrand] = useState(false);
  const [connectedBrands, setConnectedBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [productName, setProductName] = useState('');
  const [loadingBrands, setLoadingBrands] = useState(false);

  const isOwner = user?.handle === handle;
  const isBrandViewer = user?.user_type === 'brand';

  // Check if user is on Pro plan
  const isProPlan = user?.plan_id === 'influencer_pro';
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (showAddModal) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [showAddModal]);
  
  // Fetch connected brands for collaboration tagging
  const fetchConnectedBrands = useCallback(async () => {
    if (!token) return;
    setLoadingBrands(true);
    try {
      const res = await fetch(`${API_URL}/api/collaboration-content/connected-brands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConnectedBrands(data.brands || []);
      }
    } catch (err) {
      console.error('Error fetching connected brands:', err);
    } finally {
      setLoadingBrands(false);
    }
  }, [token]);

  // Fetch plan limits
  const fetchPlanLimits = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/user/plan-limits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlanLimits(data);
      }
    } catch (err) {
      console.error('Error fetching plan limits:', err);
    }
  }, [token]);

  // Fetch portfolio
  const fetchPortfolio = useCallback(async () => {
    try {
      let res;
      const headers = {};
      
      // Include auth token so brand users can see Instagram links
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      if (handle) {
        // Public view (but with auth if available)
        res = await fetch(`${API_URL}/api/influencer-portfolio/${handle}`, { headers });
      } else if (token) {
        // Own portfolio
        res = await fetch(`${API_URL}/api/influencer-portfolio`, { headers });
      }
      
      if (res && res.ok) {
        const data = await res.json();
        setInfluencer(data.influencer);
        setPortfolio(data.portfolio || []);
      } else if (res && res.status === 404) {
        setError('Influencer not found');
      }
    } catch (err) {
      console.error('Error fetching portfolio:', err);
      setError('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  }, [handle, token]);

  useEffect(() => {
    fetchPortfolio();
    if (isOwner) {
      fetchPlanLimits();
      fetchConnectedBrands();
    }
  }, [fetchPortfolio, isOwner, fetchPlanLimits, fetchConnectedBrands]);

  const isAtLimit = planLimits && planLimits.current_portfolio_items >= planLimits.max_portfolio_items;

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setNewImage(reader.result.split(',')[1]);
      setNewImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Add portfolio item (and optionally submit for brand collaboration)
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newImage) {
      setError('Please select an image');
      return;
    }
    
    // Validate brand collaboration fields if tagging a brand
    if (tagBrand) {
      if (!selectedBrandId) {
        setError('Please select a brand to tag');
        return;
      }
      if (!productName.trim()) {
        setError('Please enter the product name');
        return;
      }
    }

    setUploading(true);
    setError('');

    try {
      // First, add to personal portfolio
      const portfolioRes = await fetch(`${API_URL}/api/influencer-portfolio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          image_base64: newImage,
          description: newDescription,
          tags: newTags.split(',').map(t => t.trim()).filter(t => t)
        })
      });

      if (!portfolioRes.ok) {
        const data = await portfolioRes.json();
        setError(data.detail || 'Failed to add item');
        return;
      }
      
      // If tagging a brand, submit for collaboration approval
      if (tagBrand && selectedBrandId) {
        const collabRes = await fetch(`${API_URL}/api/collaboration-content/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            image_base64: newImage,
            brand_id: selectedBrandId,
            product_name: productName,
            caption: newDescription
          })
        });
        
        if (collabRes.ok) {
          setSuccess('Look added! Brand collaboration submitted for approval.');
        } else {
          const data = await collabRes.json();
          setSuccess('Look added to portfolio! Note: ' + (data.detail || 'Brand collaboration could not be submitted.'));
        }
      } else {
        setSuccess('Portfolio item added!');
      }
      
      // Reset form
      setShowAddModal(false);
      setNewImage(null);
      setNewImagePreview(null);
      setNewDescription('');
      setNewTags('');
      setTagBrand(false);
      setSelectedBrandId('');
      setProductName('');
      fetchPortfolio();
      fetchPlanLimits();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Delete portfolio item
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const res = await fetch(`${API_URL}/api/influencer-portfolio/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess('Portfolio item deleted');
        fetchPortfolio();
        fetchPlanLimits(); // Refresh limits after deleting
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error && !influencer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">{error}</h2>
          <Link to="/collaborations" className="mt-4 inline-block text-rose-500 hover:text-rose-600">
            Back to Collaborations
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
          {/* Logo + Bell (only for logged-in users) */}
          <div className="flex items-center space-x-3">
            {token && <NotifBell />}
            <div className="flex items-center space-x-2 cursor-default">
              <Sparkles className="w-6 h-6 text-rose-400" />
              <span className="text-xl font-bold bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">
                VirtualFit
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 flex-1">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center text-green-700">
            <Check className="w-5 h-5 mr-2" />
            {success}
          </div>
        )}
        {error && influencer && (
          <div className="mb-4" data-testid="portfolio-error-display">
            {(error.toLowerCase().includes('limit') || error.toLowerCase().includes('upgrade')) ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <span className="font-semibold">Portfolio Limit Reached</span>
                  <button onClick={() => setError('')} className="ml-auto text-amber-500 hover:text-amber-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm mb-3">{error}</p>
                <Link
                  to="/subscription"
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-lg font-medium text-sm hover:shadow-lg transition-all"
                  data-testid="influencer-portfolio-upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Plan
                </Link>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center text-red-700">
                <AlertCircle className="w-5 h-5 mr-2" />
                {error}
                <button onClick={() => setError('')} className="ml-auto">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Influencer Header */}
        {influencer && (
          <div className="bg-white rounded-2xl p-6 shadow-lg mb-8 overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-r from-rose-100 to-orange-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {influencer.profile_image ? (
                  <img 
                    src={influencer.profile_image?.startsWith('data:') ? influencer.profile_image : `data:image/jpeg;base64,${influencer.profile_image}`} 
                    alt={influencer.name} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <Users className="w-12 h-12 text-rose-400" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-gray-800 truncate" data-testid="influencer-name">{influencer.name}</h1>
                <p className="text-gray-500 truncate">@{influencer.handle}</p>
                {influencer.bio && (
                  <p className="mt-2 text-gray-600 line-clamp-3 break-all">{influencer.bio}</p>
                )}
              </div>
              
              <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
                {(influencer.store_link || influencer.affiliate_link) && (
                  <a
                    href={(influencer.store_link || influencer.affiliate_link)?.startsWith('http') ? (influencer.store_link || influencer.affiliate_link) : `https://${influencer.store_link || influencer.affiliate_link}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg flex items-center justify-center whitespace-nowrap"
                    data-testid="store-link"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Shop My Looks
                  </a>
                )}
                {/* Show Instagram to all viewers */}
                {influencer.instagram_link && (
                  <a
                    href={influencer.instagram_link?.startsWith('http') ? influencer.instagram_link : `https://instagram.com/${influencer.instagram_link.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:shadow-lg flex items-center justify-center whitespace-nowrap"
                    data-testid="instagram-link"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    Instagram
                  </a>
                )}
                {isOwner && !isAtLimit && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl flex items-center justify-center whitespace-nowrap"
                    data-testid="add-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Look
                  </button>
                )}
                {isOwner && isAtLimit && !isProPlan && (
                  <Link
                    to="/subscription"
                    className="px-4 py-2 bg-amber-100 text-amber-700 rounded-xl flex items-center justify-center text-sm whitespace-nowrap"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade for More
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Grid */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-800">
            {isOwner ? 'My Portfolio' : 'Portfolio'} ({portfolio.length}{isOwner && planLimits ? `/${planLimits.max_portfolio_items}` : ''})
          </h2>
        </div>

        {/* Limit Warning */}
        {isOwner && isAtLimit && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center text-amber-700">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            <span>
              You've reached your portfolio limit ({planLimits?.max_portfolio_items} looks). 
              {isProPlan 
                ? ' Delete existing items to add more.' 
                : ' Delete existing items or upgrade your plan to add more.'}
            </span>
          </div>
        )}

        {portfolio.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {isOwner 
                ? "You haven't added any looks yet. Add your first one!"
                : "This influencer hasn't added any looks yet."}
            </p>
            {isOwner && !isAtLimit && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 px-6 py-2 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg"
              >
                Add Your First Look
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {portfolio.map(item => (
              <div 
                key={item.id} 
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow group"
                data-testid={`portfolio-item-${item.id}`}
              >
                <div className="aspect-[3/4] relative bg-gray-50">
                  <img
                    src={item.image_url || (item.image?.startsWith('data:') ? item.image : (item.image ? `data:image/jpeg;base64,${item.image}` : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=='))}
                    alt={item.description || 'Portfolio item'}
                    className="w-full h-full object-contain"
                  />
                  {isOwner && item.source !== 'collaboration' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }}
                      className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full z-10 shadow-md"
                      data-testid={`delete-item-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  {item.source === 'collaboration' && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500/90 text-white text-xs font-medium rounded-full z-10 shadow-sm" data-testid={`collab-badge-${item.id}`}>
                      Brand Collab
                    </div>
                  )}
                </div>
                
                {(item.description || item.tags?.length > 0) && (
                  <div className="p-3">
                    {item.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 break-all">
                        <LinkifyText text={item.description} />
                      </p>
                    )}
                    {item.tags?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.tags.map((tag, idx) => (
                          <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full truncate max-w-[100px]">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          {/* Modal Container - fixed height with internal scroll */}
          <div 
            className="bg-white rounded-2xl w-full mx-2 sm:mx-4 flex flex-col"
            style={{ 
              maxWidth: '28rem',
              maxHeight: 'calc(100vh - 2rem)',
              height: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="add-item-modal"
          >
            {/* Header - Fixed */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">Add New Look</h3>
              <button 
                onClick={() => setShowAddModal(false)} 
                className="text-gray-400 hover:text-gray-600 p-1"
                type="button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto p-4" style={{ minHeight: 0 }}>
              <form onSubmit={handleAddItem} id="add-look-form">
                {/* Image Upload */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image <span className="text-red-500">*</span>
                  </label>
                  {newImagePreview ? (
                    <div className="relative w-full h-36 rounded-xl overflow-hidden">
                      <img src={newImagePreview} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => { setNewImage(null); setNewImagePreview(null); }}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="block border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-rose-400 transition-colors">
                      <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                      <span className="text-gray-500 text-sm">Click to upload</span>
                      <span className="block text-xs text-gray-400 mt-1">High-quality portrait photo, well-lit</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="image-upload-input"
                      />
                    </label>
                  )}
                </div>

              {/* Description */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Describe your look..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 resize-none"
                  rows={2}
                  data-testid="description-input"
                />
              </div>

              {/* Tags */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Tags (optional)
                </label>
                <input
                  type="text"
                  value={newTags}
                  onChange={(e) => setNewTags(e.target.value)}
                  placeholder="casual, summer"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500"
                  data-testid="tags-input"
                />
              </div>
              
              {/* Brand Collaboration Tagging */}
              <div className="p-3 bg-purple-50 rounded-xl border border-purple-100">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tagBrand}
                    onChange={(e) => setTagBrand(e.target.checked)}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    data-testid="tag-brand-checkbox"
                  />
                  <span className="ml-2 text-sm font-medium text-purple-800">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Tag a Brand
                  </span>
                </label>
                
                {tagBrand && (
                  <div className="mt-3 space-y-2">
                    {loadingBrands ? (
                      <div className="flex items-center text-purple-600 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading...
                      </div>
                    ) : connectedBrands.length === 0 ? (
                      <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg">
                        <AlertCircle className="w-3 h-3 inline mr-1" />
                        No collaborations. <Link to="/collaborations" className="underline">Connect first</Link>
                      </div>
                    ) : (
                      <>
                        <select
                          value={selectedBrandId}
                          onChange={(e) => setSelectedBrandId(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                          data-testid="select-brand-dropdown"
                        >
                          <option value="">Select brand...</option>
                          {connectedBrands.map(brand => (
                            <option key={brand.id} value={brand.id}>
                              @{brand.handle}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={productName}
                          onChange={(e) => setProductName(e.target.value)}
                          placeholder="Product name"
                          className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                          data-testid="product-name-input"
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
              </form>
            </div>

            {/* Footer - Fixed */}
            <div className="flex-shrink-0 p-4 border-t border-gray-100">
              <button
                type="submit"
                form="add-look-form"
                disabled={uploading || !newImage}
                className="w-full py-3 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl hover:shadow-lg disabled:opacity-50 flex items-center justify-center font-medium"
                data-testid="submit-item-btn"
              >
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    Add to Portfolio
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer isAuthenticated={!!user} isProPlan={isProPlan} />
    </div>
  );
}
